import { groq } from "next-sanity"
import { checkPermissions } from "@/graphql/schemas/shared/permissions/permissions"
import { DomainObjects, Operations } from "@/graphql/schemas/shared/permissions/permissionsConfig"
import { groqQueryOptions } from "@/graphql/schemas/shared/utils/groqQueryOptions"
import { AppSettings } from "@/settings"
import { getCurrentDate } from "@/utils/strings/date"

/**
 * Fetches active article notices from Sanity.
 * When sku is set, only notices for that article are returned.
 *
 * @param {{ sku?: string | null }} args
 * @param {object} context
 * @returns {Promise<Array<{ sku: string, statusName: string, description: string, variant: string }>>}
 */
export const queryArticleNotices = {
  async articleNotices(_parent, args, context) {
    const { sku, lang = AppSettings.defaultLocale() } = args
    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.ARTICLES,
      operation: Operations.READ,
    })

    const config = AppSettings.sanityConfigByLang(lang)
    const client = context.sanityClient
    client.config({ ...config, useCdn: true })

    const today = getCurrentDate()
    const { draftsFilter } = groqQueryOptions({ includeDrafts: true })
    const skuFilter = sku ? "&& article->sku == $sku" : ""

    try {
      const notices = await client.fetch(
        groq`*[_type == "articleNotice" ${draftsFilter} && expiresAt >= $today ${skuFilter}] {
          "sku": article->sku,
          statusName,
          description,
          "variant": coalesce(variant, "warning")
        }`,
        { today, ...(sku ? { sku } : {}) },
      )

      return (notices ?? []).filter((notice) => notice?.sku && notice?.statusName)
    } catch (error) {
      console.error("queryArticleNotices()", { error })
      return []
    }
  },
}
